import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { ArrowLeft, ExternalLink, RefreshCw, Languages, Linkedin, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useDarkMode } from "../hooks/useDarkMode";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import { BACKEND_URL } from "../lib/backend";

const API = BACKEND_URL;

const PROSE_CSS = `
.officina-prose p { margin: 0 0 1.1em; line-height: 1.75; }
.officina-prose h1, .officina-prose h2, .officina-prose h3 { margin: 1.6em 0 0.6em; font-weight: 600; line-height: 1.3; }
.officina-prose h2 { font-size: 1.25rem; }
.officina-prose h3 { font-size: 1.1rem; }
.officina-prose ul, .officina-prose ol { margin: 0 0 1.1em 1.4em; }
.officina-prose ul { list-style: disc; }
.officina-prose ol { list-style: decimal; }
.officina-prose li { margin-bottom: 0.4em; line-height: 1.65; }
.officina-prose a { text-decoration: underline; text-underline-offset: 2px; }
.officina-prose blockquote { border-left: 3px solid currentColor; opacity: 0.85; padding-left: 1em; margin: 0 0 1.1em; font-style: italic; }
.officina-prose img { border-radius: 12px; margin: 1.2em 0; max-width: 100%; }
.officina-prose strong { font-weight: 600; }
`;

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
}

export default function OfficinaArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [dm] = useDarkMode();

  useEffect(() => {
    axios.get(`${API}/api/officina/articles/${slug}`)
      .then(({ data }) => {
        setArticle(data);
        document.title = `${data.title_en} — Officina Turistica | TourismIntel`;
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  // hreflang pair: EN translation (self) ↔ Italian original (iter104 SEO)
  useEffect(() => {
    if (!article) return;
    const links = [
      { hreflang: "en", href: `https://tourismintel.ai/officina-turistica/${slug}` },
      { hreflang: "it", href: article.url },
    ].map(({ hreflang, href }) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = href;
      el.dataset.officinaHreflang = "1";
      document.head.appendChild(el);
      return el;
    });
    return () => links.forEach((el) => el.remove());
  }, [article, slug]);

  const c = dm
    ? { bg: "bg-[#0f1718]", card: "bg-[#1a2324]", border: "border-slate-700/50", text: "text-slate-200", muted: "text-slate-500", sub: "text-slate-300", accent: "text-cyan-400" }
    : { bg: "bg-[#f5f6f7]", card: "bg-white", border: "border-[#e7e8e9]", text: "text-[#191c1d]", muted: "text-[#6b7a7d]", sub: "text-[#3b494c]", accent: "text-[#006874]" };

  const html = article ? DOMPurify.sanitize(marked.parse(article.content_en || "")) : "";

  return (
    <div className={`min-h-screen flex flex-col ${c.bg}`}>
      <style>{PROSE_CSS}</style>
      <PublicPageNav />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-10" data-testid="officina-article-page">
        <Link to="/officina-turistica" className={`inline-flex items-center gap-1.5 text-xs font-medium mb-8 ${c.muted} hover:underline`} data-testid="officina-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> All Officina Turistica posts
        </Link>

        {notFound ? (
          <p className={`text-sm text-center py-20 ${c.muted}`} data-testid="officina-article-not-found">Article not found.</p>
        ) : !article ? (
          <div className="flex justify-center py-20"><RefreshCw className={`w-6 h-6 animate-spin ${c.muted}`} /></div>
        ) : (
          <article>
            <div className={`flex items-center gap-2 mb-4 ${c.accent}`}>
              <Languages className="w-4 h-4" />
              <span className="category-tag">Translated from Italian · Officina Turistica</span>
            </div>
            <h1 className={`editorial-heading text-3xl sm:text-4xl font-medium mb-4 leading-tight ${c.text}`} data-testid="officina-article-title">
              {article.title_en}
            </h1>
            <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 mb-6 text-sm ${c.muted}`}>
              <span className={`font-semibold ${c.accent}`} data-testid="officina-article-author">{article.author}</span>
              <span>{fmtDate(article.published_at)}</span>
              {(article.categories || []).slice(0, 4).map((cat) => (
                <span key={cat} className={`px-2 py-0.5 rounded-full border text-xs ${c.border}`}>{cat}</span>
              ))}
              <span className="flex items-center gap-1 ml-auto">
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${API}/api/preview/officina/${slug}`)}`}
                  target="_blank" rel="noopener noreferrer" title="Share on LinkedIn"
                  data-testid="officina-share-linkedin"
                  className={`p-1.5 rounded-full transition-colors ${dm ? "text-slate-400 hover:text-cyan-300 hover:bg-white/5" : "text-[#6b7a7d] hover:text-[#006874] hover:bg-[#f0f5f5]"}`}
                >
                  <Linkedin className="w-4 h-4" />
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${API}/api/preview/officina/${slug}`);
                    toast.success("Share link copied — paste it on LinkedIn for a rich card");
                  }}
                  title="Copy share link"
                  data-testid="officina-share-copy"
                  className={`p-1.5 rounded-full transition-colors ${dm ? "text-slate-400 hover:text-cyan-300 hover:bg-white/5" : "text-[#6b7a7d] hover:text-[#006874] hover:bg-[#f0f5f5]"}`}
                >
                  <Link2 className="w-4 h-4" />
                </button>
              </span>
            </div>

            {article.image_url && (
              <img src={article.image_url} alt="" className="w-full rounded-2xl mb-8 max-h-[420px] object-cover"
                onError={(e) => { e.target.style.display = "none"; }} />
            )}

            <div
              className={`officina-prose text-[15px] ${c.sub}`}
              data-testid="officina-article-content"
              dangerouslySetInnerHTML={{ __html: html }}
            />

            <div className={`mt-10 pt-6 border-t text-sm ${c.border} ${c.muted}`}>
              <p className="mb-2">
                Originally published in Italian by <span className={`font-semibold ${c.accent}`}>{article.author}</span> on Officina Turistica.
                Translation preserves the author&apos;s original voice.
              </p>
              <a href={article.url} target="_blank" rel="noreferrer" hrefLang="it"
                 className={`inline-flex items-center gap-1.5 font-semibold ${c.accent} hover:underline`}
                 data-testid="officina-article-original-link">
                Read the original (Italian) <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </article>
        )}
      </main>
      <PublicPageFooter />
    </div>
  );
}
